/**
 * User Hook — fetches the logged-in VTEX user once per page load and keeps
 * it in a module-level signal shared by every consumer (header account
 * dropdown, wishlist button).
 *
 * Mirrors the original apps/vtex/hooks/useUser.ts + context.ts pair: load on
 * first mount, refresh when the tab regains visibility (login finishes in a
 * popup or another tab).
 */
import { useEffect } from "react";
import { useStore } from "@tanstack/react-store";
import { signal } from "~/sdk/signal";
import { invoke } from "~/runtime";

export interface User {
  "@id": string;
  email: string;
  givenName?: string;
  familyName?: string;
}

const user = signal<User | null>(null);
const loading = signal(true);

let initStarted = false;

async function loadUser() {
  loading.value = true;
  try {
    const person: User | null = await invoke.vtex.loaders.user();
    user.value = person?.email ? person : null;
  } catch (err) {
    console.error("[useUser] load failed:", err);
  } finally {
    loading.value = false;
  }
}

export function useUser() {
  // The module-level `signal()` is @tanstack/store-backed and does NOT
  // auto-subscribe on `.value` reads inside render (see ~/sdk/atoms.ts) —
  // subscribe explicitly so consumers re-render when the user lands.
  useStore(user.store, (s) => s);
  useStore(loading.store, (s) => s);

  useEffect(() => {
    if (initStarted) return;
    initStarted = true;
    loadUser();
    document.addEventListener(
      "visibilitychange",
      () => document.visibilityState === "visible" && loadUser(),
    );
  }, []);

  return { user, loading };
}

export default useUser;
